import React, { useState, useEffect, useCallback } from "react";
import {
  ClipboardList, AlertTriangle, CheckCircle2, TrendingUp, Layers, BarChart3, PieChart,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Card, Button, Badge, Spinner, PageHeader, EmptyState } from "../lib/ui";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import api from "../lib/api";

const rp = (v) => (v == null ? "Rp 0" : "Rp " + Math.round(Number(v)).toLocaleString("id-ID"));
const num = (v) => Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

const PRIO_TONE = { HIGH: "danger", MEDIUM: "warn", LOW: "mute" };
const PRIO_LABEL = { HIGH: "Tinggi", MEDIUM: "Sedang", LOW: "Rendah" };

const KpiCard = ({ icon: Icon, tone, label, value, sub }) => {
  const tones = { primary: "bg-primary-light text-primary", success: "bg-success-light text-success-text", danger: "bg-danger-light text-danger-text", info: "bg-blue-100 text-blue-700" };
  return (
    <Card className="flex items-center gap-3">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={20} /></div>
      <div className="min-w-0"><p className="font-heading text-xl font-bold text-ink">{value}</p><p className="text-xs text-ink-mute">{label}</p>{sub && <p className="text-[11px] text-ink-soft">{sub}</p>}</div>
    </Card>
  );
};

// ---------------- Part A: Missing Recipe ----------------
function MissingTab() {
  const { t } = useT();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ALL");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/recipe-coverage/missing"); setData(data); }
    catch { toast.error(t("Failed to load")); }
  }, [t, toast]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex h-48 items-center justify-center"><Spinner className="h-8 w-8" /></div>;
  const s = data.summary;

  let rows = data.rows;
  if (filter === "COMPLETE") rows = rows.filter((r) => r.recipe_status === "COMPLETE");
  else if (filter === "MISSING") rows = rows.filter((r) => r.recipe_status === "MISSING");
  else if (filter === "MOST_SOLD_MISSING") rows = data.rows.filter((r) => r.recipe_status === "MISSING").slice().sort((a, b) => b.sales_quantity - a.sales_quantity);
  else if (filter === "TOP_REVENUE_MISSING") rows = data.rows.filter((r) => r.recipe_status === "MISSING").slice().sort((a, b) => b.sales_value - a.sales_value);

  const filters = [
    { id: "ALL", label: t("All") },
    { id: "COMPLETE", label: t("Recipe Complete") },
    { id: "MISSING", label: t("Missing Recipe") },
    { id: "MOST_SOLD_MISSING", label: t("Most Sold Missing") },
    { id: "TOP_REVENUE_MISSING", label: t("Top Revenue Missing") },
  ];

  return (
    <div className="space-y-5" data-testid="recipe-missing">
      <div className="grid gap-4 sm:grid-cols-4">
        <KpiCard icon={Layers} tone="primary" label={t("Total Menu")} value={s.total_menu} />
        <KpiCard icon={CheckCircle2} tone="success" label={t("Recipe Complete")} value={s.recipe_complete} />
        <KpiCard icon={AlertTriangle} tone="danger" label={t("Missing Recipe")} value={s.missing_recipe} />
        <KpiCard icon={PieChart} tone="info" label={t("Coverage")} value={`${s.coverage_pct}%`} />
      </div>

      {data.top_missing.length > 0 && (
        <Card className="border-danger/30 bg-danger-light/30" data-testid="recipe-top-missing">
          <h3 className="mb-2 flex items-center gap-2 font-heading font-bold text-danger-text"><AlertTriangle size={18} /> {t("Top Missing Recipes Requiring Action")}</h3>
          <div className="flex flex-wrap gap-2">
            {data.top_missing.slice(0, 6).map((r) => (
              <div key={r.menu_item_id} className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs">
                <span className="font-semibold text-ink">{r.menu_name}</span>
                <span className="ml-2 text-ink-soft">{num(r.sales_quantity)} {t("sold")} · {rp(r.sales_value)}</span>
                <Badge tone={PRIO_TONE[r.priority]} className="ml-2 !px-1.5 !py-0.5 !text-[10px]">{t(PRIO_LABEL[r.priority])}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="mb-3 inline-flex flex-wrap gap-1 rounded-xl border border-line bg-white p-1">
          {filters.map((f) => (
            <button key={f.id} data-testid={`recipe-filter-${f.id}`} onClick={() => setFilter(f.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f.id ? "bg-primary text-white" : "text-ink-soft hover:bg-stone-100"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recipe-missing-table">
            <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
              <tr><th className="py-2 pr-3">{t("Menu Name")}</th><th className="py-2 pr-3">{t("Code")}</th><th className="py-2 pr-3">{t("Recipe Status")}</th><th className="py-2 pr-3 text-right">{t("Sales Qty")}</th><th className="py-2 pr-3 text-right">{t("Sales Value")}</th><th className="py-2 pr-3">{t("Priority")}</th></tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.menu_item_id} data-testid={`recipe-row-${r.menu_item_id}`}>
                  <td className="py-2.5 pr-3 font-medium text-ink">{r.menu_name}</td>
                  <td className="py-2.5 pr-3 text-xs text-ink-soft">{r.menu_code || "—"}</td>
                  <td className="py-2.5 pr-3">
                    {r.recipe_status === "COMPLETE"
                      ? <Badge tone="success">{t("Complete")}</Badge>
                      : <Badge tone="danger">{t("Missing Recipe")}</Badge>}
                  </td>
                  <td className="py-2.5 pr-3 text-right">{num(r.sales_quantity)}</td>
                  <td className="py-2.5 pr-3 text-right font-semibold text-ink">{rp(r.sales_value)}</td>
                  <td className="py-2.5 pr-3">{r.priority ? <Badge tone={PRIO_TONE[r.priority]}>{t(PRIO_LABEL[r.priority])}</Badge> : <span className="text-ink-mute">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ---------------- Part B: Coverage Impact ----------------
function ImpactTab() {
  const { t } = useT();
  const toast = useToast();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/recipe-coverage/impact").then(({ data }) => setData(data)).catch(() => toast.error(t("Failed to load")));
  }, [t, toast]);

  if (!data) return <div className="flex h-48 items-center justify-center"><Spinner className="h-8 w-8" /></div>;
  const s = data.summary, d = data.distortion;

  return (
    <div className="space-y-5" data-testid="recipe-impact">
      <div className="grid gap-4 sm:grid-cols-4">
        <KpiCard icon={BarChart3} tone="primary" label={t("Total Sales")} value={rp(s.total_sales)} sub={`${num(s.covered_units + s.uncovered_units)} ${t("units")}`} />
        <KpiCard icon={CheckCircle2} tone="success" label={t("Covered Sales")} value={rp(s.covered_sales)} sub={`${num(s.covered_units)} ${t("units")}`} />
        <KpiCard icon={AlertTriangle} tone="danger" label={t("Uncovered Sales")} value={rp(s.uncovered_sales)} sub={`${num(s.uncovered_units)} ${t("units")}`} />
        <KpiCard icon={PieChart} tone="info" label={t("Coverage")} value={`${s.coverage_pct}%`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="recipe-distortion">
          <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><AlertTriangle size={18} className="text-warn-text" /> {t("Potential Distortion")}</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-xl bg-warn-light px-4 py-3">
              <div><p className="text-sm font-semibold text-warn-text">{t("Inventory Distortion")}</p><p className="text-xs text-ink-soft">{t("Units sold without depleting inventory")}</p></div>
              <p className="font-heading text-lg font-bold text-warn-text">{num(d.potential_inventory_distortion_units)} {t("units")}</p>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-danger-light px-4 py-3">
              <div><p className="text-sm font-semibold text-danger-text">{t("Margin Distortion")}</p><p className="text-xs text-ink-soft">{t("Sales with no HPP / margin recorded")}</p></div>
              <p className="font-heading text-lg font-bold text-danger-text">{rp(d.potential_margin_distortion_value)}</p>
            </div>
            <p className="text-center text-xs text-ink-mute">{d.uncovered_pct}% {t("of sales is not backed by a recipe")}</p>
          </div>
        </Card>

        <Card data-testid="recipe-trend">
          <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><TrendingUp size={18} /> {t("Monthly Coverage Trend")}</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly_trend} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="coverage_pct" stroke="#f97316" strokeWidth={2.5} dot={{ r: 3 }} name={t("Coverage")} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 text-center text-xs text-ink-mute">{t("Track progress toward 100% recipe coverage")}</p>
        </Card>
      </div>
    </div>
  );
}

// ---------------- Page ----------------
export default function RecipeCoverage() {
  const { t } = useT();
  const [tab, setTab] = useState("missing");
  const tabs = [
    { id: "missing", label: t("Missing Recipes"), icon: ClipboardList },
    { id: "impact", label: t("Coverage Impact"), icon: BarChart3 },
  ];
  return (
    <div data-testid="recipe-coverage-page">
      <PageHeader title={t("Recipe Coverage")} subtitle={t("Recipe completeness & sales coverage intelligence")} icon={ClipboardList} />
      <div className="mb-5 inline-flex rounded-xl border border-line bg-white p-1">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`recipe-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === tb.id ? "bg-primary text-white shadow-sm" : "text-ink-soft hover:bg-stone-100"}`}>
            <tb.icon size={16} /> {tb.label}
          </button>
        ))}
      </div>
      {tab === "missing" ? <MissingTab /> : <ImpactTab />}
    </div>
  );
}
